import { useState } from 'react';
import { initials } from '../lib/format.js';
import { getDisplayName } from '../lib/identity.js';

/**
 * "Qui etes-vous ?"
 *
 * Shown once per browser per outing, before anything can be changed. Picking an
 * existing name is what makes the app work across devices: enter your car on a
 * laptop, open the link on a phone, choose yourself again, and it is still yours
 * to edit.
 *
 * Typing a name that already exists resolves to that same person rather than
 * creating a second one, so the roster cannot quietly fill up with duplicates.
 */

const ROLE_LABEL = {
  driver: 'conduit une voiture',
  passenger: 'passager',
  none: 'pas encore inscrit',
};

export default function IdentityGate({ trip, onChoose, onCancel, busy, error, current }) {
  const [name, setName] = useState(() => getDisplayName());
  const [adding, setAdding] = useState(false);

  const members = [...(trip.members ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  const showList = members.length > 0 && !adding;

  const submitNew = (event) => {
    event.preventDefault();
    if (!name.trim() || busy) return;
    onChoose({ name: name.trim() });
  };

  return (
    <div className="stack gap-16">
      {error && <div className="toast error" style={{ borderRadius: 10 }}>⚠ <span>{error}</span></div>}

      <p className="muted">
        Choisissez votre nom pour rejoindre <strong>{trip.title}</strong>. Vous pourrez retrouver et
        modifier vos choix depuis n'importe quel appareil en reprenant le meme nom.
      </p>

      {showList && (
        <>
          <div className="section-label">Participants deja inscrits</div>
          <div className="identity-list">
            {members.map((member) => (
              <button
                key={member.id}
                type="button"
                className={`identity-row ${current === member.id ? 'is-current' : ''}`}
                onClick={() => onChoose({ memberId: member.id, name: member.name })}
                disabled={busy}
              >
                <span className="identity-avatar">{initials(member.name)}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className="identity-name truncate">{member.name}</span>
                  <span className="identity-role truncate">
                    {ROLE_LABEL[member.role]}
                    {member.withDriver ? ` de ${member.withDriver}` : ''}
                  </span>
                </span>
                {current === member.id && <span className="chip chip-accent">vous</span>}
              </button>
            ))}
          </div>
        </>
      )}

      {adding || members.length === 0 ? (
        <form onSubmit={submitNew} className="stack gap-10">
          <div className="field">
            <label htmlFor="ig-name">
              {members.length === 0 ? 'Votre nom' : 'Nom du nouveau participant'}
            </label>
            <input
              id="ig-name"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Prenom Nom"
              maxLength={80}
              autoComplete="name"
              required
            />
            <span className="field-hint">
              Si ce nom est deja dans la liste, vous reprendrez simplement cette identite.
            </span>
          </div>
          <div className="row gap-8">
            <button type="submit" className="btn btn-primary" disabled={!name.trim() || busy}>
              {busy && <span className="spinner" />}
              Continuer
            </button>
            {members.length > 0 && (
              <button type="button" className="btn btn-ghost" onClick={() => setAdding(false)} disabled={busy}>
                Retour a la liste
              </button>
            )}
            {onCancel && members.length === 0 && (
              <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>
                Plus tard
              </button>
            )}
          </div>
        </form>
      ) : (
        <div className="row gap-8 wrap">
          <button type="button" className="btn" onClick={() => setAdding(true)} disabled={busy}>
            + Je ne suis pas dans la liste
          </button>
          {onCancel && (
            <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>
              Juste regarder
            </button>
          )}
        </div>
      )}
    </div>
  );
}
